import {
  authorize,
  logout,
  getUserEntitlements,
  isSessionMigrationRequiredError,
} from '@/services/auth';
import { User } from '@/types/user';
import { StateCreator } from 'zustand/vanilla';
import { useWorkspaceStore } from '@/store/workspace';
import { performCompleteLogout } from '@/lib/logout-utils';
import { UserStore } from '../../store';

export interface UserAuthAction {
  logout: () => Promise<void>;
  authorize: () => Promise<void>;
  setUser: (user: User | undefined) => void;
  setAuthenticated: (isSignedIn: boolean) => void;
  setInitialized: (isLoaded: boolean) => void;
  fetchUserEntitlements: (
    workspaceId: string,
    organizationId?: string
  ) => Promise<{ screens: string[]; features: string[]; status: 'loaded' | 'error' }>;
  /** @deprecated Use fetchUserEntitlements. */
  fetchUserScreens: (workspaceId: string, organizationId?: string) => Promise<string[]>;
  clearEntitlements: (workspaceId?: string) => void;
  setScreens: (screens: string[]) => void;
  setLoggingOut: (isLoggingOut: boolean) => void;
}

export const createAuthSlice: StateCreator<
  UserStore,
  [['zustand/devtools', never]],
  [],
  UserAuthAction
> = (set, get) => ({
  logout: async () => {
    // Step 1: Set logging out flag to prevent other components from triggering auth
    set({ isLoggingOut: true });

    // Step 2: Clear Zustand store state immediately to prevent race conditions
    set({
      user: undefined,
      isSignedIn: false,
      isLoaded: false,
      screens: [],
      features: [],
      screensLoaded: false,
      entitlementsStatus: 'idle',
      entitlementsWorkspaceId: undefined,
    });

    // Step 3: Clear in-memory workspace data before logout, but keep the user's preferred
    // workspace id so it can be restored after the next login.
    useWorkspaceStore.getState().clearWorkspace({ preserveSelection: true });

    // Step 4: Clear all local storage, session storage, and cookies
    performCompleteLogout();

    // Step 5: Redirect to logout API endpoint
    const host = process.env.NEXT_PUBLIC_APP_URL;
    if (host) {
      logout({ post_logout_redirect_uri: host });
      return;
    }
    logout({});
  },
  authorize: async () => {
    const next =
      typeof window !== 'undefined'
        ? window.location.pathname + window.location.search + window.location.hash
        : '/chat';
    authorize({
      next,
      referer: typeof window !== 'undefined' ? window.location.href : undefined,
      project_id: '1',
    });
  },
  setUser: user => {
    set({ user });
  },
  setAuthenticated: isSignedIn => {
    set({ isSignedIn });
  },
  setInitialized: isLoaded => {
    set({ isLoaded });
  },
  fetchUserEntitlements: async (workspaceId, organizationId) => {
    set({
      screens: [],
      features: [],
      screensLoaded: false,
      entitlementsStatus: 'loading',
      entitlementsWorkspaceId: workspaceId,
    });

    try {
      const response = await getUserEntitlements(workspaceId, organizationId);
      const screens = response.data?.[0]?.screens || [];
      const features = response.data?.[0]?.features || [];

      if (get().entitlementsWorkspaceId === workspaceId) {
        set({
          screens,
          features,
          screensLoaded: true,
          entitlementsStatus: 'loaded',
        });
      }

      return { screens, features, status: 'loaded' };
    } catch (error) {
      if (isSessionMigrationRequiredError(error)) {
        await get().authorize();
        return { screens: [], features: [], status: 'error' };
      }

      console.error('Failed to fetch user entitlements:', error);
      if (get().entitlementsWorkspaceId === workspaceId) {
        set({
          screens: [],
          features: [],
          screensLoaded: true,
          entitlementsStatus: 'error',
        });
      }
      return { screens: [], features: [], status: 'error' };
    }
  },
  fetchUserScreens: async (workspaceId, organizationId) => {
    const { screens } = await get().fetchUserEntitlements(workspaceId, organizationId);
    return screens;
  },
  clearEntitlements: workspaceId => {
    set({
      screens: [],
      features: [],
      screensLoaded: false,
      entitlementsStatus: workspaceId ? 'loading' : 'idle',
      entitlementsWorkspaceId: workspaceId,
    });
  },
  setScreens: screens => {
    set({ screens, screensLoaded: true, entitlementsStatus: 'loaded' });
  },
  setLoggingOut: isLoggingOut => {
    set({ isLoggingOut });
  },
});
